import { storageFactory } from '@/services/storage/storageFactory';
import { Artifact, ArtifactSearchResult, CreateArtifactData, UpdateArtifactData } from '@/types/artifact';
import { StorageService } from '@/types/storage';
import { v4 as uuidv4 } from 'uuid';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface ArtifactStore {
  artifacts: Artifact[];
  currentArtifactId: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  searchResults: ArtifactSearchResult[];
  searchQuery: string;

  storageService: StorageService;

  loadArtifacts: () => Promise<void>;
  createArtifact: (data: CreateArtifactData) => Promise<Artifact>;
  updateArtifact: (id: string, updates: UpdateArtifactData) => Promise<void>;
  deleteArtifact: (id: string) => Promise<void>;
  setCurrentArtifact: (id: string | null) => void;

  filterArtifacts: (params: { query?: string }) => Promise<void>;
  clearFilter: () => void;

  importArtifacts: (artifacts: Artifact[]) => Promise<void>;
  exportArtifacts: () => Promise<Artifact[]>;
  clearAllArtifacts: () => Promise<void>;

  setError: (error: string | null) => void;
  clearError: () => void;

  get currentArtifact(): Artifact | null;
}

export const useArtifactStore = create<ArtifactStore>()(
  devtools(
    (set, get) => ({
      artifacts: [],
      currentArtifactId: null,
      isLoading: false,
      isSaving: false,
      error: null,
      searchResults: [],
      searchQuery: '',
    
      storageService: storageFactory.createStorageService(),

      loadArtifacts: async () => {
        set({ isLoading: true, error: null });
        try {
          const artifacts = await get().storageService.getAllArtifacts();
          set({ artifacts, isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load artifacts',
            isLoading: false
          });
        }
      },

      createArtifact: async (data: CreateArtifactData) => {
        const newArtifactId = uuidv4();
        const now = new Date();

        const optimisticArtifact: Artifact = {
          id: newArtifactId,
          title: data.title.trim() || 'Untitled Artifact',
          htmlContent: data.htmlContent,
          createdAt: now,
          updatedAt: now,
          metadata: {
            size: data.htmlContent.length,
            hasExternalResources: false,
            dependencies: [],
            characterCount: data.htmlContent.length,
          }
        };

        set(state => ({
          artifacts: [optimisticArtifact, ...state.artifacts],
          currentArtifactId: newArtifactId,
        }));

        try {
          const savedArtifact = await get().storageService.createArtifact(optimisticArtifact);

          set(state => ({
            artifacts: state.artifacts.map(a => a.id === newArtifactId ? savedArtifact : a),
          }));

          return savedArtifact;
        } catch (error) {
          set(state => ({
            artifacts: state.artifacts.filter(a => a.id !== newArtifactId),
            error: error instanceof Error ? error.message : 'Failed to create artifact',
          }));
          throw error;
        }
      },

      updateArtifact: async (id: string, updates: UpdateArtifactData) => {
        const originalArtifacts = get().artifacts;
        const artifactToUpdate = originalArtifacts.find(a => a.id === id);
        if (!artifactToUpdate) return;

        set({ isSaving: true });

        try {
          const optimisticArtifact = {
            ...artifactToUpdate,
            ...updates,
            updatedAt: new Date()
          };

          set({
            artifacts: originalArtifacts.map(a =>
              a.id === id ? optimisticArtifact : a
            ),
          });

          await get().storageService.updateArtifact(id, updates);
          
          set({ isSaving: false });

        } catch (error) {
          set({
            artifacts: originalArtifacts,
            error: error instanceof Error ? error.message : 'Failed to update artifact',
            isSaving: false,
          });
        }
      },

      deleteArtifact: async (id: string) => {
        const originalArtifacts = get().artifacts;

        set(state => ({
          artifacts: state.artifacts.filter(artifact => artifact.id !== id),
          currentArtifactId: state.currentArtifactId === id ? null : state.currentArtifactId,
        }));

        try {
          await get().storageService.deleteArtifact(id);
        } catch (error) {
          set({
            artifacts: originalArtifacts,
            error: error instanceof Error ? error.message : 'Failed to delete artifact',
          });
        }
      },

      setCurrentArtifact: (id: string | null) => {
        set({ currentArtifactId: id });
      },

      filterArtifacts: async ({ query }) => {
        set({
          isLoading: true,
          searchQuery: query || ''
        });
        try {
          let results: Artifact[] = [];
          if (query) {
            const searchResults = await get().storageService.searchArtifacts(query);
            results = searchResults.map(r => r.artifact);
          } else {
            results = await get().storageService.getAllArtifacts();
          }
          set({ artifacts: results, searchResults: [], isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Filter failed',
            isLoading: false
          });
        }
      },

      clearFilter: () => {
        set({ searchQuery: '' });
        get().loadArtifacts();
      },

      importArtifacts: async (artifacts: Artifact[]) => {
        set({ isLoading: true, error: null });
        try {
          await get().storageService.importArtifacts(artifacts);
          await get().loadArtifacts();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Import failed',
            isLoading: false
          });
        }
      },

      exportArtifacts: async () => {
        try {
          return await get().storageService.exportArtifacts();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Export failed'
          });
          return [];
        }
      },

      clearAllArtifacts: async () => {
        set({ isLoading: true, error: null });
        try {
          await get().storageService.clearAll();
          set({
            artifacts: [],
            currentArtifactId: null,
            searchResults: [],
            isLoading: false
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Clear all failed',
            isLoading: false
          });
        }
      },

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      get currentArtifact() {
        const { artifacts, currentArtifactId } = get();
        return artifacts.find(artifact => artifact.id === currentArtifactId) || null;
      },

    }),
    {
      name: 'artifact-store',
    }
  )
); 